import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Spinner,
    Alert,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const FormInputField = ({ label, name, type = "text", rows, value, onChange, error }) => (
    <Form.Group className="mb-3">
        {label && (
            <Form.Label className="fw-semibold small text-muted text-uppercase">
                {label}
            </Form.Label>
        )}
        <Form.Control
            type={type !== "textarea" ? type : undefined}
            as={type === "textarea" ? "textarea" : undefined}
            rows={rows}
            name={name}
            value={value}
            onChange={onChange}
            isInvalid={!!error}
            className="bg-light border-0 py-2 px-3"
        />
        <Form.Control.Feedback type="invalid">
            {Array.isArray(error) ? error[0] : error}
        </Form.Control.Feedback>
    </Form.Group>
);

const SiteSettingsPage = () => {
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);
    const [savingAll, setSavingAll] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        header_marquees: [], // Array of { title, text } for dynamic key-value handling
        site_address: "",
        site_email: "",
        site_phone: "",
        social_links: {
            facebook: "",
            instagram: "",
            x: "",
            tiktok: "",
            pinterest: "",
        },
    });

    const getAuthHeaders = () => {
        const token = localStorage.getItem("authToken");
        return {
            Authorization: token ? `Bearer ${token}` : "",
        };
    };

    const getSiteSettingsData = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/site-settings`,
                { headers: getAuthHeaders() }
            );

            const data = response.data?.data;

            if (data) {
                // Convert header_marquees Object { "Title": "Text" } into Array [{ title, text }]
                const marqueesArray = data.header_marquees && typeof data.header_marquees === "object"
                    ? Object.entries(data.header_marquees).map(([title, text]) => ({ title, text }))
                    : [];

                setForm({
                    header_marquees: marqueesArray,
                    site_address: data.site_address || "",
                    site_email: data.site_email || "",
                    site_phone: data.site_phone || "",
                    social_links: {
                        facebook: data.social_links?.facebook || "",
                        instagram: data.social_links?.instagram || "",
                        x: data.social_links?.x || "",
                        tiktok: data.social_links?.tiktok || "",
                        pinterest: data.social_links?.pinterest || "",
                        ...data.social_links,
                    },
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load Site Settings.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getSiteSettingsData();
    }, []);

    // Basic Input Handler
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    // Social Links Handler
    const handleSocialChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            social_links: {
                ...prev.social_links,
                [name]: value,
            },
        }));
    };

    // Header Marquees Key-Value Pair Handlers
    const handleMarqueeChange = (index, field, value) => {
        const updatedMarquees = [...form.header_marquees];
        updatedMarquees[index][field] = value;
        setForm((prev) => ({ ...prev, header_marquees: updatedMarquees }));
    };

    const addMarqueeItem = () => {
        setForm((prev) => ({
            ...prev,
            header_marquees: [...prev.header_marquees, { title: "", text: "" }],
        }));
    };

    const removeMarqueeItem = (index) => {
        const updatedMarquees = form.header_marquees.filter((_, i) => i !== index);
        setForm((prev) => ({ ...prev, header_marquees: updatedMarquees }));
    };

    // Format header_marquees array back to Object key-value for API store
    const preparePayloadValue = (key) => {
        if (key === "header_marquees") {
            const objectVal = {};
            form.header_marquees.forEach((item) => {
                if (item.title.trim()) {
                    objectVal[item.title.trim()] = item.text;
                }
            });
            return objectVal;
        }
        return form[key];
    };

    // Save individual setting key
    const saveSettingByKey = async (key) => {
        setMessage("");
        setError("");
        setSavingKey(key);

        const value = preparePayloadValue(key);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/web-manager/site-settings`,
                { key, value },
                { headers: getAuthHeaders() }
            );

            setMessage(response.data?.message || `Setting '${key}' saved successfully.`);
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
                setError(err.response.data.message || "Please fix validation errors.");
            } else {
                setError(err.response?.data?.message || `Failed to update ${key}.`);
            }
        } finally {
            setSavingKey(null);
        }
    };

    // Save All Settings sequentially
    const handleSaveAll = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");
        setSavingAll(true);

        const keysToSave = [
            "site_email",
            "site_phone",
            "site_address",
            "social_links",
            "header_marquees",
        ];

        try {
            for (const key of keysToSave) {
                const value = preparePayloadValue(key);
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/site-settings`,
                    { key, value },
                    { headers: getAuthHeaders() }
                );
            }
            setMessage("All site settings saved successfully!");
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save all settings.");
        } finally {
            setSavingAll(false);
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Site Settings</h2>
                        <p className="text-muted mb-0">
                            Manage contact details, social links, and header announcements.
                        </p>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="primary"
                            onClick={handleSaveAll}
                            disabled={savingAll || loading}
                            className="px-4 py-2 fw-semibold text-uppercase"
                        >
                            {savingAll ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving All...
                                </>
                            ) : (
                                "Save All Settings"
                            )}
                        </Button>
                    </Col>
                </Row>

                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                        <div className="mt-3 text-muted">Loading settings...</div>
                    </div>
                ) : (
                    <>
                        {/* CONTACT INFORMATION */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">
                                Contact Information
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <FormInputField
                                            label="Site Email"
                                            name="site_email"
                                            type="email"
                                            value={form.site_email}
                                            onChange={handleChange}
                                            error={errors.site_email}
                                        />
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={savingKey === "site_email"}
                                            onClick={() => saveSettingByKey("site_email")}
                                        >
                                            {savingKey === "site_email" ? "Saving..." : "Save Email"}
                                        </Button>
                                    </Col>

                                    <Col md={6}>
                                        <FormInputField
                                            label="Site Phone"
                                            name="site_phone"
                                            value={form.site_phone}
                                            onChange={handleChange}
                                            error={errors.site_phone}
                                        />
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={savingKey === "site_phone"}
                                            onClick={() => saveSettingByKey("site_phone")}
                                        >
                                            {savingKey === "site_phone" ? "Saving..." : "Save Phone"}
                                        </Button>
                                    </Col>

                                    <Col md={12} className="mt-4">
                                        <FormInputField
                                            label="Site Address"
                                            name="site_address"
                                            type="textarea"
                                            rows={2}
                                            value={form.site_address}
                                            onChange={handleChange}
                                            error={errors.site_address}
                                        />
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={savingKey === "site_address"}
                                            onClick={() => saveSettingByKey("site_address")}
                                        >
                                            {savingKey === "site_address" ? "Saving..." : "Save Address"}
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* SOCIAL LINKS */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0">
                                Social Links
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <FormInputField
                                            label="Facebook"
                                            name="facebook"
                                            value={form.social_links.facebook}
                                            onChange={handleSocialChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <FormInputField
                                            label="Instagram"
                                            name="instagram"
                                            value={form.social_links.instagram}
                                            onChange={handleSocialChange}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <FormInputField
                                            label="X (Twitter)"
                                            name="x"
                                            value={form.social_links.x}
                                            onChange={handleSocialChange}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <FormInputField
                                            label="TikTok"
                                            name="tiktok"
                                            value={form.social_links.tiktok}
                                            onChange={handleSocialChange}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <FormInputField
                                            label="Pinterest"
                                            name="pinterest"
                                            value={form.social_links.pinterest}
                                            onChange={handleSocialChange}
                                        />
                                    </Col>
                                </Row>
                                <Button
                                    className="mt-2"
                                    variant="primary"
                                    disabled={savingKey === "social_links"}
                                    onClick={() => saveSettingByKey("social_links")}
                                >
                                    {savingKey === "social_links" ? "Saving..." : "Save Social Links"}
                                </Button>
                            </Card.Body>
                        </Card>

                        {/* HEADER MARQUEES */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3 fw-bold border-0 d-flex justify-content-between align-items-center">
                                <span>Header Marquee Announcements</span>
                                <Button size="sm" variant="secondary" onClick={addMarqueeItem}>
                                    + Add Announcement
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {form.header_marquees.length === 0 ? (
                                    <p className="text-muted mb-3">No marquee messages added.</p>
                                ) : (
                                    form.header_marquees.map((item, index) => (
                                        <Row key={index} className="align-items-center mb-3">
                                            <Col md={4}>
                                                <FormInputField
                                                    label={index === 0 ? "Title / Label" : ""}
                                                    name={`marquee_title_${index}`}
                                                    value={item.title}
                                                    onChange={(e) =>
                                                        handleMarqueeChange(index, "title", e.target.value)
                                                    }
                                                />
                                            </Col>
                                            <Col md={7}>
                                                <FormInputField
                                                    label={index === 0 ? "Message" : ""}
                                                    name={`marquee_text_${index}`}
                                                    value={item.text}
                                                    onChange={(e) =>
                                                        handleMarqueeChange(index, "text", e.target.value)
                                                    }
                                                />
                                            </Col>
                                            <Col md={1} className="d-flex align-items-center mt-2">
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => removeMarqueeItem(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </Col>
                                        </Row>
                                    ))
                                )}
                                <Button
                                    className="mt-2"
                                    variant="primary"
                                    disabled={savingKey === "header_marquees"}
                                    onClick={() => saveSettingByKey("header_marquees")}
                                >
                                    {savingKey === "header_marquees" ? "Saving..." : "Save Header Marquees"}
                                </Button>
                            </Card.Body>
                        </Card>
                    </>
                )}
            </Container>
        </PageLayout>
    );
};

export default SiteSettingsPage;